import { c as _c } from "react/compiler-runtime";
import { Operator, type SupportingFact, TWELVE_HOURS_MS } from '@netpurpose/types';
import { httpStatusCode, valueIsDefined } from '@netpurpose/utils';
import { keepPreviousData, useMutation, useQueries, useQuery } from '@tanstack/react-query';
import { deburr } from 'lodash';
import qs from 'qs';
import { type Dispatch, type SetStateAction, useEffect, useMemo, useState } from 'react';
import { useDebounceValue } from 'usehooks-ts';
import { apiConfig } from '../../config';
import { ApiError, getApiErrorMessage } from '../../errors';
import { factsClient, GeneratedFactApi } from '../../GeneratedApi';
import { type CreateEntity, type EntityWithOptionalDetails, ExportFormats, type EntityBreakdownByFunds as GeneratedEntityBreakdownByFunds, type EstimatedData as GeneratedEstimatedData, type LatestData as GeneratedLatestData, type GetTimeseriesFactsErrors, type GetTimeseriesFactsResponses, type InsightTotals, type MergeTwoEntities, type PortfolioWithEntityDetail, type RevenueDeltasRequestSegment, type RevenueDeltasResponseSegment, type Rollup, type SegmentTaxonMappingConfidence } from '../../generated/facts';
import { type EntityPortfolio, reverseEntityPortfolioFieldMap, transformEntityPortfolio } from '../../models';
import type { ReverseFieldMap } from '../../queryBuilder';
import { type Camelize, camelToSnakeKeys, snakeToCamelKeys } from '../../utils';
import { useStreamDownload } from '../useStreamDownload';
import { getPaginationConfig, useUrlApiConnector } from '../useUrlApiConnector';
import { useAsyncTaskWithAction } from './useAsyncTasks';
import { type Entity, type EntityWithQuestionDetailsFilters, reverseEntityWithQuestionDetailsFieldMap, reverseTimeSeriesFieldMap, type TimeSeriesFact, transformEntity, transformEntityDataByQuestionId, transformTimeSeriesData } from './utils';
import { emptyOnNotFound } from './utils/emptyOnNotFound';
export type EntityWithFilters = Entity & {
  searchTerm: string;
  marketCap?: number;
  contributingRevenuePct?: number;
  potentialRevenuePct?: number;
  negativeRevenuePct?: number;
};
const entityTableToApiFieldMap: Partial<ReverseFieldMap<keyof EntityWithFilters | 'factsetId'>> = {
  id: 'entity_id',
  name: 'name',
  type: 'type',
  primaryIsin: 'primary_isin',
  headquarters: 'headquarters',
  sector: 'sector',
  industry: 'industry',
  marketCap: 'market_cap',
  hasResearch: 'has_research',
  alignedSdgs: 'sdg_goals',
  factsetId: 'identifiers.value',
  searchTerm: 'search_term',
  contributingRevenuePct: 'contributing_revenue_pct',
  potentialRevenuePct: 'potential_revenue_pct',
  negativeRevenuePct: 'negative_revenue_pct',
  inactiveSince: 'with_inactive',
  themes: 'themes',
  isCovered: 'is_covered',
  isSdiCovered: 'is_sdi_covered'
};
export const PAGINATED_ENTITIES_QUERY_CACHE_KEY = 'paginatedEntities';
export const usePaginatedEntities = t0 => {
  const $ = _c(35);
  const {
    perPage,
    defaultParams,
    defaultSortBy,
    useUrlSync: t1,
    enabled: t2,
    staleTime
  } = t0;
  const useUrlSync = t1 === undefined ? true : t1;
  const enabled = t2 === undefined ? true : t2;
  let t3;
  if ($[0] !== defaultParams) {
    t3 = defaultParams ? {
      defaultParams
    } : {};
    $[0] = defaultParams;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== perPage) {
    t4 = perPage ? {
      perPage
    } : {};
    $[2] = perPage;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== defaultSortBy) {
    t5 = defaultSortBy ? {
      defaultSortBy
    } : {};
    $[4] = defaultSortBy;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t3 || $[7] !== t4 || $[8] !== t5 || $[9] !== useUrlSync) {
    t6 = {
      tableToApiFieldMap: entityTableToApiFieldMap,
      urlKey: PAGINATED_ENTITIES_QUERY_CACHE_KEY,
      ...t3,
      ...t4,
      ...t5,
      urlSyncEnabled: useUrlSync
    };
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
    $[9] = useUrlSync;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const {
    queryString,
    filterConfig,
    initialPaginationConfig
  } = useUrlApiConnector(t6);
  let t7;
  let t8;
  if ($[11] !== queryString) {
    t7 = [PAGINATED_ENTITIES_QUERY_CACHE_KEY, queryString];
    t8 = () => GeneratedFactApi.entities.listEntities({
      query: {
        q: queryString
      }
    });
    $[11] = queryString;
    $[12] = t7;
    $[13] = t8;
  } else {
    t7 = $[12];
    t8 = $[13];
  }
  let t9;
  if ($[14] !== staleTime) {
    t9 = staleTime ? {
      staleTime
    } : {};
    $[14] = staleTime;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== enabled || $[17] !== t7 || $[18] !== t8 || $[19] !== t9) {
    t10 = {
      queryKey: t7,
      queryFn: t8,
      placeholderData: keepPreviousData,
      ...t9,
      enabled
    };
    $[16] = enabled;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const t11 = useQuery(t10);
  let data;
  let rest;
  if ($[21] !== t11) {
    ({
      data,
      ...rest
    } = t11);
    $[21] = t11;
    $[22] = data;
    $[23] = rest;
  } else {
    data = $[22];
    rest = $[23];
  }
  const t12 = data?.total;
  let t13;
  if ($[24] !== initialPaginationConfig || $[25] !== t12) {
    t13 = getPaginationConfig({
      numResults: t12,
      paginationConfig: initialPaginationConfig
    });
    $[24] = initialPaginationConfig;
    $[25] = t12;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  const paginationConfig = t13;
  let t14;
  if ($[27] !== data) {
    t14 = data?.results ? data.results.map(_temp) : undefined;
    $[27] = data;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  const transformedEntities = t14;
  const t15 = data?.total;
  let t16;
  if ($[29] !== filterConfig || $[30] !== paginationConfig || $[31] !== rest || $[32] !== t15 || $[33] !== transformedEntities) {
    t16 = {
      ...rest,
      data: transformedEntities,
      filterConfig,
      paginationConfig,
      total: t15
    };
    $[29] = filterConfig;
    $[30] = paginationConfig;
    $[31] = rest;
    $[32] = t15;
    $[33] = transformedEntities;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  return t16;
};
export const useCreateEntity = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp2
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useUpdateEntity = t0 => {
  const $ = _c(9);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    method: t2,
    onSuccess
  } = t1;
  const method = t2 === undefined ? "put" : t2;
  let t3;
  if ($[2] !== method) {
    t3 = t4 => {
      const {
        entityId,
        payload
      } = t4;
      return method === "put" ? GeneratedFactApi.entities.putUpdateEntity({
        body: camelToSnakeKeys(payload),
        path: {
          entity_id: entityId
        }
      }) : GeneratedFactApi.entities.patchUpdateEntity({
        body: camelToSnakeKeys(payload),
        path: {
          entity_id: entityId
        }
      });
    };
    $[2] = method;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== onSuccess) {
    t4 = () => onSuccess?.();
    $[4] = onSuccess;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t3 || $[7] !== t4) {
    t5 = {
      mutationFn: t3,
      onSuccess: t4
    };
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return useMutation(t5);
};
export const useMergeEntities = t0 => {
  const $ = _c(4);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    onSuccess
  } = t1;
  let t2;
  if ($[2] !== onSuccess) {
    t2 = {
      mutationFn: _temp3,
      onSuccess: () => onSuccess?.()
    };
    $[2] = onSuccess;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return useMutation(t2);
};
export const ENTITIES_BY_ID_QUERY_CACHE_KEY = 'entitiesByIds';
export const useEntitiesById = t0 => {
  const $ = _c(21);
  const {
    entityIds,
    withBonds: t1
  } = t0;
  const withBonds = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== entityIds) {
    t2 = new Set(entityIds);
    $[0] = entityIds;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t2) {
    t3 = [...t2].map(_temp4);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const uniqueIds = t3;
  let t4;
  if ($[4] !== uniqueIds) {
    t4 = [ENTITIES_BY_ID_QUERY_CACHE_KEY, uniqueIds];
    $[4] = uniqueIds;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== uniqueIds || $[7] !== withBonds) {
    t5 = () => GeneratedFactApi.entities.listEntities({
      query: {
        q: `${uniqueIds.join("&")}${withBonds ? "&with_bonds=true" : ""}`
      }
    }).then(_temp5);
    $[6] = uniqueIds;
    $[7] = withBonds;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = uniqueIds.length > 0;
  let t7;
  if ($[9] !== t4 || $[10] !== t5 || $[11] !== t6) {
    t7 = {
      queryKey: t4,
      queryFn: t5,
      enabled: t6,
      staleTime: TWELVE_HOURS_MS
    };
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = useQuery(t7);
  let data;
  let rest;
  if ($[13] !== t8) {
    ({
      data,
      ...rest
    } = t8);
    $[13] = t8;
    $[14] = data;
    $[15] = rest;
  } else {
    data = $[14];
    rest = $[15];
  }
  let t9;
  if ($[16] !== data) {
    t9 = data?.reduce(_temp6, {} as Record<number, Entity>);
    $[16] = data;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const recordsById = t9;
  let t10;
  if ($[18] !== recordsById || $[19] !== rest) {
    t10 = {
      data: recordsById,
      ...rest
    };
    $[18] = recordsById;
    $[19] = rest;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  return t10;
};
type EntityParams = {
  with_details?: boolean;
  with_bonds?: boolean;
  with_inactive?: boolean;
  with_revenue_segment_details?: boolean;
  with_sdi?: boolean;
};
const stringifyEntityParams = (params: EntityParams) => qs.stringify(params, {
  skipNulls: true,
  arrayFormat: 'repeat'
});
const getEntity = ({
  entityId,
  queryString,
  withSdi
}: {
  entityId: number;
  queryString: string;
  withSdi: boolean;
}) => GeneratedFactApi.entities.getEntity({
  path: {
    entity_id: entityId
  },
  ...(!!queryString ? {
    query: {
      q: queryString
    }
  } : {}),
  ...(withSdi ? {
    headers: {
      'x-np-feature': ['promoted']
    }
  } : {})
});

// An entity outside the SDI dataset 404s when SDI fields are requested, so retry without
// them rather than losing every other detail the page needs.
const getEntityWithSdiFallback = async ({
  entityId,
  queryString,
  fallbackQueryString
}: {
  entityId: number;
  queryString: string;
  fallbackQueryString: string;
}) => {
  try {
    return await getEntity({
      entityId,
      queryString,
      withSdi: true
    });
  } catch (error) {
    if (error instanceof ApiError && error.status === httpStatusCode.notFound) {
      return getEntity({
        entityId,
        queryString: fallbackQueryString,
        withSdi: false
      });
    }
    throw error;
  }
};
export const useEntity = t0 => {
  const $ = _c(25);
  const {
    entityId,
    defaultParams,
    staleTime,
    enabled: t1
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  let params;
  let t2;
  if ($[0] !== defaultParams) {
    params = {
      with_details: false,
      with_bonds: false,
      with_sdi: false,
      with_inactive: true,
      with_revenue_segment_details: false,
      ...defaultParams
    };
    t2 = stringifyEntityParams(params);
    $[0] = defaultParams;
    $[1] = params;
    $[2] = t2;
  } else {
    params = $[1];
    t2 = $[2];
  }
  const queryString = t2;
  let t3;
  if ($[3] !== entityId || $[4] !== queryString) {
    t3 = ["entity", entityId, queryString];
    $[3] = entityId;
    $[4] = queryString;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== entityId || $[7] !== params || $[8] !== queryString) {
    t4 = () => {
      if (!entityId) {
        return;
      }
      return params.with_sdi ? getEntityWithSdiFallback({
        entityId,
        queryString,
        fallbackQueryString: stringifyEntityParams({
          ...params,
          with_sdi: false
        })
      }) : getEntity({
        entityId,
        queryString,
        withSdi: false
      });
    };
    $[6] = entityId;
    $[7] = params;
    $[8] = queryString;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = !!entityId && !!enabled;
  let t6;
  if ($[10] !== staleTime) {
    t6 = staleTime ? {
      staleTime
    } : {};
    $[10] = staleTime;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t3 || $[13] !== t4 || $[14] !== t5 || $[15] !== t6) {
    t7 = {
      queryKey: t3,
      queryFn: t4,
      enabled: t5,
      ...t6
    };
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const t8 = useQuery(t7);
  let data;
  let rest;
  if ($[17] !== t8) {
    ({
      data,
      ...rest
    } = t8);
    $[17] = t8;
    $[18] = data;
    $[19] = rest;
  } else {
    data = $[18];
    rest = $[19];
  }
  let t9;
  if ($[20] !== data) {
    t9 = data ? transformEntity(snakeToCamelKeys(data)) : undefined;
    $[20] = data;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const transformedEntity = t9;
  let t10;
  if ($[22] !== rest || $[23] !== transformedEntity) {
    t10 = {
      ...rest,
      data: transformedEntity
    };
    $[22] = rest;
    $[23] = transformedEntity;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  return t10;
};
export const useInactiveEntity = t0 => {
  const $ = _c(15);
  const {
    entityId
  } = t0;
  let t1;
  let t2;
  if ($[0] !== entityId) {
    t1 = ["inactiveEntity", entityId];
    t2 = () => entityId ? GeneratedFactApi.entities.getInactiveEntity({
      path: {
        entity_id: entityId
      }
    }) : null;
    $[0] = entityId;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  const t3 = !!entityId;
  let t4;
  if ($[3] !== t1 || $[4] !== t2 || $[5] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3
    };
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = useQuery(t4);
  let data;
  let rest;
  if ($[7] !== t5) {
    ({
      data,
      ...rest
    } = t5);
    $[7] = t5;
    $[8] = data;
    $[9] = rest;
  } else {
    data = $[8];
    rest = $[9];
  }
  let t6;
  if ($[10] !== data) {
    t6 = snakeToCamelKeys(data);
    $[10] = data;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== rest || $[13] !== t6) {
    t7 = {
      ...rest,
      data: t6
    };
    $[12] = rest;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
};
export const useEntityPortfolios = t0 => {
  const $ = _c(24);
  const {
    entityId
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      tableToApiFieldMap: reverseEntityPortfolioFieldMap,
      urlKey: "entityPortfolios"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    queryString,
    filterConfig,
    initialPaginationConfig
  } = useUrlApiConnector(t1);
  let t2;
  if ($[1] !== queryString) {
    t2 = ["entityPortfolios", queryString];
    $[1] = queryString;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== entityId || $[4] !== queryString) {
    t3 = () => GeneratedFactApi.entities.getEntityPortfolios({
      path: {
        entity_id: entityId
      },
      query: {
        q: queryString
      }
    });
    $[3] = entityId;
    $[4] = queryString;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = !!entityId;
  let t5;
  if ($[6] !== t2 || $[7] !== t3 || $[8] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      placeholderData: keepPreviousData,
      enabled: t4
    };
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = useQuery(t5);
  let data;
  let rest;
  if ($[10] !== t6) {
    ({
      data,
      ...rest
    } = t6);
    $[10] = t6;
    $[11] = data;
    $[12] = rest;
  } else {
    data = $[11];
    rest = $[12];
  }
  const t7 = data?.length;
  let t8;
  if ($[13] !== initialPaginationConfig || $[14] !== t7) {
    t8 = getPaginationConfig({
      numResults: t7,
      paginationConfig: initialPaginationConfig
    });
    $[13] = initialPaginationConfig;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const paginationConfig = t8;
  let t9;
  if ($[16] !== data) {
    t9 = data ? data.map(_temp7) : undefined;
    $[16] = data;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const transformedEntityPortfolios = t9;
  const t10 = data?.length;
  let t11;
  if ($[18] !== filterConfig || $[19] !== paginationConfig || $[20] !== rest || $[21] !== t10 || $[22] !== transformedEntityPortfolios) {
    t11 = {
      ...rest,
      data: transformedEntityPortfolios,
      filterConfig,
      paginationConfig,
      total: t10
    };
    $[18] = filterConfig;
    $[19] = paginationConfig;
    $[20] = rest;
    $[21] = t10;
    $[22] = transformedEntityPortfolios;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  return t11;
};
export const ENTITY_BREAKDOWN_BY_FUNDS_QUERY_CACHE_KEY = 'entityBreakdownByFunds';
export const useEntityBreakdownByFunds = t0 => {
  const $ = _c(20);
  const {
    entityId,
    portfolioId,
    enabled: t1
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      entityValue: "entity_value",
      entityWeight: "entity_weight"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      tableToApiFieldMap: t2,
      urlKey: ENTITY_BREAKDOWN_BY_FUNDS_QUERY_CACHE_KEY,
      defaultParams: {
        limit: 9999
      }
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const {
    queryString,
    filterConfig
  } = useUrlApiConnector(t3);
  let t4;
  let t5;
  if ($[2] !== entityId || $[3] !== portfolioId || $[4] !== queryString) {
    t4 = [ENTITY_BREAKDOWN_BY_FUNDS_QUERY_CACHE_KEY, entityId, portfolioId, queryString];
    t5 = () => entityId && portfolioId ? GeneratedFactApi.entities.getEntityBreakdownByFunds({
      path: {
        entity_id: entityId,
        portfolio_id: portfolioId
      },
      ...(!!queryString ? {
        query: {
          q: queryString
        }
      } : {})
    }) : undefined;
    $[2] = entityId;
    $[3] = portfolioId;
    $[4] = queryString;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  const t6 = !!(entityId && portfolioId && enabled);
  let t7;
  if ($[7] !== t4 || $[8] !== t5 || $[9] !== t6) {
    t7 = {
      queryKey: t4,
      queryFn: t5,
      enabled: t6,
      staleTime: TWELVE_HOURS_MS
    };
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const t8 = useQuery(t7);
  let data;
  let rest;
  if ($[11] !== t8) {
    ({
      data,
      ...rest
    } = t8);
    $[11] = t8;
    $[12] = data;
    $[13] = rest;
  } else {
    data = $[12];
    rest = $[13];
  }
  let t9;
  if ($[14] !== data) {
    t9 = data ? snakeToCamelKeys(data) : undefined;
    $[14] = data;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const transformedEntityBreakdown = t9;
  let t10;
  if ($[16] !== filterConfig || $[17] !== rest || $[18] !== transformedEntityBreakdown) {
    t10 = {
      ...rest,
      data: transformedEntityBreakdown,
      filterConfig
    };
    $[16] = filterConfig;
    $[17] = rest;
    $[18] = transformedEntityBreakdown;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  return t10;
};
export type EntityBreakdownByFunds = ReturnType<typeof useEntityBreakdownByFunds>['data'];
type GeneratedTimeseriesReturnType = ReturnType<typeof GeneratedFactApi.entities.getTimeseriesFacts>;
const getTimeseriesFacts = (entity_id: number, query: Record<string, unknown> = {}): GeneratedTimeseriesReturnType =>
// NOTE: this is supposed to be GeneratedFactApi.entities.getTimeseriesFacts, but the filters
// we send (question_id, question.name, unit.name, theme, sdg_goal) are read by the backend
// straight off the raw request via its QueryField dependency, with operator prefixes like
// `:theme` and `!theme`. FastAPI can't introspect that, so they're absent from the spec and
// from the generated query type — hence going through the client directly.
factsClient.get<GetTimeseriesFactsResponses, GetTimeseriesFactsErrors, true, 'data'>({
  path: {
    entity_id
  },
  query,
  responseStyle: 'data',
  // The client only attaches the bearer token when the request declares it,
  // and going direct means we don't inherit it from the generated SDK method.
  security: [{
    scheme: 'bearer',
    type: 'http'
  }],
  url: '/api/v1/entities/{entity_id}/timeseries'
});
export const useEntityTimeSeries = t0 => {
  const $ = _c(22);
  const {
    defaultParams
  } = t0;
  let t1;
  if ($[0] !== defaultParams) {
    t1 = {
      tableToApiFieldMap: reverseTimeSeriesFieldMap,
      urlKey: "entityTimeseries",
      defaultParams: {
        ...defaultParams,
        limit: 9999
      }
    };
    $[0] = defaultParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    queryString,
    filterConfig
  } = useUrlApiConnector(t1);
  const {
    entity_id
  } = defaultParams;
  let t2;
  if ($[2] !== queryString) {
    t2 = qs.parse(queryString);
    $[2] = queryString;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const query = t2;
  let t3;
  if ($[4] !== queryString) {
    t3 = ["entityTimeseries", queryString];
    $[4] = queryString;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== entity_id || $[7] !== query) {
    t4 = () => getTimeseriesFacts(entity_id, query);
    $[6] = entity_id;
    $[7] = query;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = !!(entity_id && queryString && defaultParams.metrics_config_id);
  let t6;
  if ($[9] !== t3 || $[10] !== t4 || $[11] !== t5) {
    t6 = {
      queryKey: t3,
      queryFn: t4,
      enabled: t5,
      staleTime: TWELVE_HOURS_MS,
      placeholderData: keepPreviousData
    };
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const t7 = useQuery(t6);
  let data;
  let rest;
  if ($[13] !== t7) {
    ({
      data,
      ...rest
    } = t7);
    $[13] = t7;
    $[14] = data;
    $[15] = rest;
  } else {
    data = $[14];
    rest = $[15];
  }
  data?.results;
  let t8;
  if ($[16] !== data?.results) {
    t8 = data?.results ? data?.results.map(_temp8) : undefined;
    $[16] = data?.results;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const transformedTimeSeriesData = t8;
  let t9;
  if ($[18] !== filterConfig || $[19] !== rest || $[20] !== transformedTimeSeriesData) {
    t9 = {
      ...rest,
      data: transformedTimeSeriesData,
      filterConfig
    };
    $[18] = filterConfig;
    $[19] = rest;
    $[20] = transformedTimeSeriesData;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
};
export const ENTITY_DATA_BY_QUESTION_ID_QUERY_CACHE_KEY = 'entityDataByQuestionId';
export const useEntityDataByQuestionId = t0 => {
  const $ = _c(55);
  const {
    defaultParams,
    questionId,
    withSdi,
    withTotalRow,
    urlSyncEnabled,
    hasSnapshotsAccess,
    defaultSortBy,
    defaultSortReverse
  } = t0;
  let t1;
  if ($[0] !== defaultSortBy) {
    t1 = defaultSortBy ? {
      defaultSortBy
    } : {};
    $[0] = defaultSortBy;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== defaultSortReverse) {
    t2 = defaultSortReverse ? {
      defaultSortReverse
    } : {};
    $[2] = defaultSortReverse;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== defaultParams || $[5] !== t1 || $[6] !== t2 || $[7] !== urlSyncEnabled || $[8] !== withTotalRow) {
    t3 = {
      tableToApiFieldMap: reverseEntityWithQuestionDetailsFieldMap,
      urlKey: ENTITY_DATA_BY_QUESTION_ID_QUERY_CACHE_KEY,
      defaultParams,
      withTotalRow,
      urlSyncEnabled,
      ...t1,
      ...t2
    };
    $[4] = defaultParams;
    $[5] = t1;
    $[6] = t2;
    $[7] = urlSyncEnabled;
    $[8] = withTotalRow;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const {
    queryString,
    filterConfig,
    initialPaginationConfig
  } = useUrlApiConnector(t3);
  let t4;
  if ($[10] !== withSdi) {
    t4 = withSdi ? ["promoted"] : [];
    $[10] = withSdi;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== defaultParams || $[13] !== hasSnapshotsAccess) {
    t5 = hasSnapshotsAccess && !!defaultParams?.["holding.portfolio_id"] ? ["snapshot-attribution"] : [];
    $[12] = defaultParams;
    $[13] = hasSnapshotsAccess;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t4 || $[16] !== t5) {
    t6 = [...t4, ...t5];
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const npFeatures = t6;
  let t7;
  if ($[18] !== hasSnapshotsAccess || $[19] !== queryString || $[20] !== questionId || $[21] !== withSdi) {
    t7 = [ENTITY_DATA_BY_QUESTION_ID_QUERY_CACHE_KEY, queryString, questionId, hasSnapshotsAccess, withSdi];
    $[18] = hasSnapshotsAccess;
    $[19] = queryString;
    $[20] = questionId;
    $[21] = withSdi;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== npFeatures || $[24] !== queryString || $[25] !== questionId) {
    t8 = () => questionId ? GeneratedFactApi.entities.listQuestionEntitiesData({
      path: {
        question_id: questionId
      },
      query: {
        q: queryString
      },
      ...(npFeatures.length ? {
        headers: {
          "x-np-feature": npFeatures
        }
      } : {})
    }) : undefined;
    $[23] = npFeatures;
    $[24] = queryString;
    $[25] = questionId;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  const t9 = !!questionId && !!queryString;
  let t10;
  if ($[27] !== t7 || $[28] !== t8 || $[29] !== t9) {
    t10 = {
      queryKey: t7,
      queryFn: t8,
      enabled: t9,
      staleTime: TWELVE_HOURS_MS,
      placeholderData: keepPreviousData
    };
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  const t11 = useQuery(t10);
  let data;
  let rest;
  if ($[31] !== t11) {
    ({
      data,
      ...rest
    } = t11);
    $[31] = t11;
    $[32] = data;
    $[33] = rest;
  } else {
    data = $[32];
    rest = $[33];
  }
  data?.results;
  let t12;
  if ($[34] !== data?.results) {
    t12 = data?.results ? data?.results.map(_temp9) : undefined;
    $[34] = data?.results;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  const transformedEntityDataByQuestionId = t12;
  const t13 = data?.total;
  let t14;
  if ($[36] !== initialPaginationConfig || $[37] !== t13) {
    t14 = getPaginationConfig({
      numResults: t13,
      paginationConfig: initialPaginationConfig
    });
    $[36] = initialPaginationConfig;
    $[37] = t13;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  const paginationConfig = t14;
  const t15 = data?.totals.company;
  let t16;
  if ($[39] !== t15) {
    t16 = snakeToCamelKeys(t15);
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  const t17 = data?.totals.portfolio;
  let t18;
  if ($[41] !== t17) {
    t18 = snakeToCamelKeys(t17);
    $[41] = t17;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== t16 || $[44] !== t18) {
    t19 = {
      company: t16,
      portfolio: t18
    };
    $[43] = t16;
    $[44] = t18;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== data || $[47] !== t19 || $[48] !== transformedEntityDataByQuestionId) {
    t20 = {
      ...data,
      results: transformedEntityDataByQuestionId,
      totals: t19
    };
    $[46] = data;
    $[47] = t19;
    $[48] = transformedEntityDataByQuestionId;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  let t21;
  if ($[50] !== filterConfig || $[51] !== paginationConfig || $[52] !== rest || $[53] !== t20) {
    t21 = {
      ...rest,
      data: t20,
      filterConfig,
      paginationConfig
    };
    $[50] = filterConfig;
    $[51] = paginationConfig;
    $[52] = rest;
    $[53] = t20;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  return t21;
};
export const useEntityDataByQuestionIdSnapshots = t0 => {
  const $ = _c(17);
  const {
    questionId,
    withSdi,
    portfolioId,
    snapshotIds
  } = t0;
  let t1;
  if ($[0] !== portfolioId || $[1] !== questionId || $[2] !== snapshotIds || $[3] !== withSdi) {
    let t2;
    if ($[5] !== portfolioId || $[6] !== questionId || $[7] !== withSdi) {
      t2 = snapshotId => ({
        queryKey: [ENTITY_DATA_BY_QUESTION_ID_QUERY_CACHE_KEY, questionId, portfolioId, snapshotId, withSdi],
        queryFn: () => questionId ? GeneratedFactApi.entities.listQuestionEntitiesData({
          path: {
            question_id: questionId
          },
          query: {
            q: `holding.portfolio_id=${portfolioId}&holding.snapshot_id=${snapshotId}`
          },
          ...(withSdi ? {
            headers: {
              "x-np-feature": ["promoted"]
            }
          } : {})
        }).then(snapshotData => [snapshotId, snapshotData.totals] as [number, InsightTotals]) : null,
        enabled: !!questionId,
        staleTime: TWELVE_HOURS_MS,
        placeholderData: keepPreviousData
      });
      $[5] = portfolioId;
      $[6] = questionId;
      $[7] = withSdi;
      $[8] = t2;
    } else {
      t2 = $[8];
    }
    t1 = snapshotIds.map(t2);
    $[0] = portfolioId;
    $[1] = questionId;
    $[2] = snapshotIds;
    $[3] = withSdi;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[9] !== t1) {
    t2 = {
      queries: t1,
      combine: _temp12
    };
    $[9] = t1;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const t3 = useQueries(t2);
  let data;
  let rest;
  if ($[11] !== t3) {
    ({
      data,
      ...rest
    } = t3);
    $[11] = t3;
    $[12] = data;
    $[13] = rest;
  } else {
    data = $[12];
    rest = $[13];
  }
  let t4;
  if ($[14] !== data || $[15] !== rest) {
    t4 = {
      ...rest,
      data
    };
    $[14] = data;
    $[15] = rest;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
};
export const useCreateEntities = () => {
  return useAsyncTaskWithAction(_temp14);
};
export const useEntityEstimationsSummary = t0 => {
  const $ = _c(2);
  const {
    entityId
  } = t0;
  let t1;
  if ($[0] !== entityId) {
    t1 = {
      queryKey: ["entityEstimationsSummary", entityId],
      queryFn: () => GeneratedFactApi.entities.getEntityEstimationsSummary({
        path: {
          entity_id: entityId
        }
      }).then(snakeToCamelKeys),
      staleTime: TWELVE_HOURS_MS
    };
    $[0] = entityId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return useQuery(t1);
};
export type EstimatedData = Camelize<GeneratedEstimatedData>;
export const useEntityEstimatedData = t0 => {
  const $ = _c(13);
  const {
    entityId,
    questionId,
    year,
    technologyIds,
    enabled
  } = t0;
  let t1;
  if ($[0] !== entityId || $[1] !== questionId || $[2] !== year) {
    t1 = ["entityEstimatedData", entityId, questionId, year];
    $[0] = entityId;
    $[1] = questionId;
    $[2] = year;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== entityId || $[5] !== questionId || $[6] !== technologyIds || $[7] !== year) {
    t2 = () => year ? GeneratedFactApi.entities.getEstimatedData({
      path: {
        entity_id: entityId,
        question_id: questionId,
        year
      },
      ...(technologyIds ? {
        query: {
          technology_ids: technologyIds
        }
      } : {})
    }).then(_temp15) : undefined;
    $[4] = entityId;
    $[5] = questionId;
    $[6] = technologyIds;
    $[7] = year;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const t3 = !!(entityId && questionId && year && enabled);
  let t4;
  if ($[9] !== t1 || $[10] !== t2 || $[11] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      staleTime: TWELVE_HOURS_MS
    };
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return useQuery(t4);
};
export const useEntityLatestData = t0 => {
  const $ = _c(9);
  const {
    entityId,
    questionId,
    cutoffDate: t1,
    enabled
  } = t0;
  const cutoffDate = t1 === undefined ? null : t1;
  let t2;
  let t3;
  if ($[0] !== cutoffDate || $[1] !== entityId || $[2] !== questionId) {
    t2 = ["latestData", entityId, questionId, cutoffDate];
    t3 = () => GeneratedFactApi.entities.getLatestData({
      path: {
        entity_id: entityId,
        question_id: questionId
      },
      query: {
        cutoff_date: cutoffDate
      }
    }).then(_temp16);
    $[0] = cutoffDate;
    $[1] = entityId;
    $[2] = questionId;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  let t4;
  if ($[5] !== enabled || $[6] !== t2 || $[7] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3,
      enabled,
      staleTime: TWELVE_HOURS_MS
    };
    $[5] = enabled;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return useQuery(t4);
};
export const useGetItemsWithEntityNames = t0 => {
  const $ = _c(15);
  const {
    items
  } = t0;
  let t1;
  if ($[0] !== items) {
    t1 = items.map(_temp17).filter(valueIsDefined);
    $[0] = items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const entityIds = t1;
  let t2;
  if ($[2] !== entityIds) {
    t2 = {
      entityIds,
      withBonds: true
    };
    $[2] = entityIds;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    data: entitiesById,
    isFetching
  } = useEntitiesById(t2);
  if (isFetching) {
    let t3;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = [];
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    let t4;
    if ($[5] !== isFetching) {
      t4 = {
        data: t3,
        isFetching
      };
      $[5] = isFetching;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    return t4;
  }
  let t3;
  if ($[7] !== entitiesById || $[8] !== items) {
    let t4;
    if ($[10] !== entitiesById) {
      t4 = item_0 => {
        const entityName = item_0?.entityId ? entitiesById?.[item_0?.entityId]?.name : "";
        return {
          ...item_0,
          ...(entityName ? {
            entityName
          } : {})
        };
      };
      $[10] = entitiesById;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    t3 = items.map(t4);
    $[7] = entitiesById;
    $[8] = items;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const itemsWithEntityNames = t3;
  let t4;
  if ($[12] !== isFetching || $[13] !== itemsWithEntityNames) {
    t4 = {
      data: itemsWithEntityNames,
      isFetching
    };
    $[12] = isFetching;
    $[13] = itemsWithEntityNames;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
};
export const useEntitiesSearch = t0 => {
  const $ = _c(19);
  const {
    enabled: t1,
    useUrlSync,
    defaultParams,
    staleTime
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  let t2;
  if ($[0] !== defaultParams) {
    t2 = defaultParams ? {
      defaultParams
    } : {};
    $[0] = defaultParams;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== staleTime) {
    t3 = staleTime ? {
      staleTime
    } : {};
    $[2] = staleTime;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== enabled || $[5] !== t2 || $[6] !== t3 || $[7] !== useUrlSync) {
    t4 = {
      perPage: 10,
      useUrlSync,
      enabled,
      ...t2,
      ...t3
    };
    $[4] = enabled;
    $[5] = t2;
    $[6] = t3;
    $[7] = useUrlSync;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const {
    data,
    filterConfig,
    isFetching
  } = usePaginatedEntities(t4);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm] = useDebounceValue(searchTerm, 300);
  let t5;
  if ($[9] !== debouncedSearchTerm || $[10] !== filterConfig) {
    t5 = () => {
      filterConfig.setFilters("name", [{
        operator: Operator.Contains,
        value: deburr(debouncedSearchTerm) || undefined
      }]);
    };
    $[9] = debouncedSearchTerm;
    $[10] = filterConfig;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== debouncedSearchTerm) {
    t6 = [debouncedSearchTerm];
    $[12] = debouncedSearchTerm;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  useEffect(t5, t6);
  let t7;
  if ($[14] !== data) {
    t7 = data || [];
    $[14] = data;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== isFetching || $[17] !== t7) {
    t8 = {
      data: t7,
      isFetching,
      onEntitySearch: setSearchTerm
    };
    $[16] = isFetching;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
};
const defaultUploadErrorMessage = 'An error occurred. Please check your file and try again.';
export const useUploadSectorToTaxonomy = t0 => {
  const $ = _c(2);
  const {
    onError
  } = t0;
  let t1;
  if ($[0] !== onError) {
    t1 = {
      mutationFn: _temp18,
      onError: err => onError(getApiErrorMessage(err, defaultUploadErrorMessage))
    };
    $[0] = onError;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return useMutation(t1);
};
export const useUploadTaxonomyAlignment = t0 => {
  const $ = _c(2);
  const {
    onError
  } = t0;
  let t1;
  if ($[0] !== onError) {
    t1 = {
      mutationFn: _temp19,
      onError: err => onError(getApiErrorMessage(err, defaultUploadErrorMessage))
    };
    $[0] = onError;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return useMutation(t1);
};
export const useEntityActivitiesSDGRevenueAlignment = t0 => {
  const $ = _c(15);
  const {
    entityId,
    enabled: t1
  } = t0;
  const enabled = t1 === undefined ? true : t1;
  let t2;
  let t3;
  if ($[0] !== entityId) {
    t2 = ["entityActivitiesSDGRevenueAlignment", entityId];
    t3 = () => entityId ? emptyOnNotFound(() => GeneratedFactApi.entities.getEntityActivitiesSdgRevenueAlignment({
      path: {
        entity_id: entityId
      }
    })) : undefined;
    $[0] = entityId;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  const t4 = enabled && !!entityId;
  let t5;
  if ($[3] !== t2 || $[4] !== t3 || $[5] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      enabled: t4,
      staleTime: TWELVE_HOURS_MS
    };
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = useQuery(t5);
  let data;
  let rest;
  if ($[7] !== t6) {
    ({
      data,
      ...rest
    } = t6);
    $[7] = t6;
    $[8] = data;
    $[9] = rest;
  } else {
    data = $[8];
    rest = $[9];
  }
  let t7;
  if ($[10] !== data) {
    t7 = data ? snakeToCamelKeys(data) : undefined;
    $[10] = data;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== rest || $[13] !== t7) {
    t8 = {
      ...rest,
      data: t7
    };
    $[12] = rest;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  return t8;
};
export type EntityActivitiesSDGRevenueAlignment = NonNullable<ReturnType<typeof useEntityActivitiesSDGRevenueAlignment>['data']>;
export type EntityActivitiesSDGRevenueAlignmentActivities = NonNullable<EntityActivitiesSDGRevenueAlignment['activities']>;
export const useUploadSegmentToTaxonomy = t0 => {
  const $ = _c(2);
  const {
    onError
  } = t0;
  let t1;
  if ($[0] !== onError) {
    t1 = {
      mutationFn: _temp20,
      onError: err => onError(getApiErrorMessage(err, defaultUploadErrorMessage))
    };
    $[0] = onError;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return useMutation(t1);
};
export const useExportEntity = t0 => {
  const $ = _c(7);
  const {
    entityId,
    onSuccess
  } = t0;
  let t1;
  if ($[0] !== entityId) {
    t1 = () => GeneratedFactApi.entities.exportEntity({
      path: {
        entity_id: entityId
      }
    });
    $[0] = entityId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== onSuccess) {
    t2 = downloadableFileRequest => onSuccess(downloadableFileRequest.result_id);
    $[2] = onSuccess;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = {
      mutationFn: t1,
      onSuccess: t2
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return useMutation(t3);
};
export const useEntitiesLookup = () => {
  return useAsyncTaskWithAction(_temp22);
};
export const useEntitiesLookupSFDR = () => {
  return useAsyncTaskWithAction(_temp24);
};
export const useEntityActivitiesSdgRevenueAlignmentByTaxon = t0 => {
  const $ = _c(8);
  const {
    entityId,
    taxonId,
    enabled
  } = t0;
  let t1;
  let t2;
  if ($[0] !== entityId || $[1] !== taxonId) {
    t1 = ["entityActivitiesSdgRevenueAlignmentByTaxon", entityId, taxonId];
    t2 = () => taxonId ? GeneratedFactApi.entities.getEntityActivitiesSdgRevenueAlignmentByTaxon({
      path: {
        entity_id: entityId,
        taxon_id: taxonId
      }
    }).then(snakeToCamelKeys) : undefined;
    $[0] = entityId;
    $[1] = taxonId;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  const t3 = enabled && !!taxonId;
  let t4;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      staleTime: TWELVE_HOURS_MS
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return useQuery(t4);
};
export type EntityActivityContributionChild = NonNullable<ReturnType<typeof useEntityActivitiesSdgRevenueAlignmentByTaxon>['data']>['children'][number];
export const useExportEntityActivitiesXlsx = t0 => {
  const $ = _c(3);
  const {
    entityId,
    entityName
  } = t0;
  const t1 = `${apiConfig.factsHost}/api/v1/entities/sdg_revenue_alignment/${entityId}/activities/export`;
  const t2 = `${entityName} - SDG Revenue Activities`;
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = {
      url: t1,
      downloadName: t2
    };
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  return useStreamDownload(t3);
};
export const useExportEntitySdiRevenueXlsx = t0 => {
  const $ = _c(3);
  const {
    entityId,
    entityName
  } = t0;
  const t1 = `${apiConfig.factsHost}/api/v1/revenue/sdi/entity/${entityId}/export`;
  const t2 = `${entityName} - SDI Revenue Activities`;
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = {
      url: t1,
      downloadName: t2
    };
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  return useStreamDownload(t3);
};
export const useExportEntityDashboardXlsx = t0 => {
  const $ = _c(6);
  const {
    entityId,
    entityName,
    metricConfigId,
    metricConfigName
  } = t0;
  const t1 = `${entityName} - Dashboard - ${metricConfigName}`;
  let t2;
  if ($[0] !== entityId || $[1] !== metricConfigId) {
    t2 = JSON.stringify({
      entity_id: entityId,
      export_options: {
        metrics_config_id: metricConfigId
      }
    });
    $[0] = entityId;
    $[1] = metricConfigId;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1 || $[4] !== t2) {
    t3 = {
      url: `${apiConfig.factsHost}/api/v1/entities/dashboard/export`,
      downloadName: t1,
      body: t2
    };
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return useStreamDownload(t3);
};
export const useEntitySdiRationale = t0 => {
  const $ = _c(7);
  const {
    entityId
  } = t0;
  let t1;
  let t2;
  if ($[0] !== entityId) {
    t1 = ["entitySdiRationale", entityId];
    t2 = () => entityId ? GeneratedFactApi.entities.getEntitySdiRationale({
      path: {
        entity_id: entityId
      }
    }) : undefined;
    $[0] = entityId;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  const t3 = !!entityId;
  let t4;
  if ($[3] !== t1 || $[4] !== t2 || $[5] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      staleTime: TWELVE_HOURS_MS,
      enabled: t3
    };
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return useQuery(t4);
};
const REVENUE_DELTAS_QUERY_KEY = 'revenueDeltas';
const buildSegments = (supportingFacts: SupportingFact[]): RevenueDeltasRequestSegment[] => {
  return supportingFacts.filter(sf => !sf.isTotal && sf.recordedValue.unit !== 'NOT FOUND').map(sf => ({
    supporting_fact_id: sf.id,
    recorded_value: [sf.recordedValue.value ?? null, sf.recordedValue.unit],
    taxon_id: sf.taxonId as number,
    name: sf.segment?.instance ?? '',
    mapping_confidence: sf.mappingConfidence as SegmentTaxonMappingConfidence,
    external_comment: sf.externalComment ?? '',
    derived: sf.isDerived
  }));
};
export const useRevenueDeltas = t0 => {
  const $ = _c(11);
  const {
    entityId,
    year,
    supportingFacts
  } = t0;
  let t1;
  if ($[0] !== supportingFacts) {
    t1 = buildSegments(supportingFacts);
    $[0] = supportingFacts;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const segments = t1;
  let t2;
  let t3;
  if ($[2] !== entityId || $[3] !== segments || $[4] !== year) {
    t2 = [REVENUE_DELTAS_QUERY_KEY, entityId, year, segments];
    t3 = () => GeneratedFactApi.entities.calculateRevenueDeltas({
      body: {
        entity_id: entityId as number,
        year: year as number,
        segments
      }
    }).then(snakeToCamelKeys);
    $[2] = entityId;
    $[3] = segments;
    $[4] = year;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  const t4 = Boolean(entityId) && Boolean(year) && segments.length > 0;
  let t5;
  if ($[7] !== t2 || $[8] !== t3 || $[9] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      enabled: t4
    };
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return useQuery(t5);
};
export type RevenueDeltas = NonNullable<ReturnType<typeof useRevenueDeltas>['data']>;
export type RevenueDeltasSegment = Camelize<RevenueDeltasResponseSegment>;
function _temp(entity) {
  return transformEntity(snakeToCamelKeys(entity));
}
function _temp2(payload) {
  return GeneratedFactApi.entities.createEntity({
    body: camelToSnakeKeys(payload)
  });
}
function _temp3(payload) {
  return GeneratedFactApi.entities.mergeEntities({
    body: camelToSnakeKeys(payload)
  });
}
function _temp4(id) {
  return `ids=${id}`;
}
function _temp5(t0) {
  const {
    results
  } = t0;
  return snakeToCamelKeys(results || []).map(transformEntity);
}
function _temp6(acc, record) {
  return {
    [record.id]: record,
    ...acc
  };
}
function _temp7(entityPortfolio) {
  return transformEntityPortfolio(snakeToCamelKeys(entityPortfolio));
}
function _temp8(d) {
  return transformTimeSeriesData(snakeToCamelKeys(d));
}
function _temp9(d) {
  return transformEntityDataByQuestionId(snakeToCamelKeys(d));
}
function _temp10(acc, val) {
  if (!val.data) {
    return acc;
  }
  return {
    ...acc,
    [val.data[0]]: snakeToCamelKeys(val.data[1])
  };
}
function _temp11(result) {
  return result.isPending;
}
function _temp12(results) {
  return {
    data: results.reduce(_temp10, {} as Record<number, Camelize<InsightTotals>>),
    isPending: results.some(_temp11)
  };
}
function _temp13(data) {
  return data.result_id;
}
function _temp14(values) {
  return GeneratedFactApi.entities.createEntities({
    body: {
      entities_csv: values.file,
      cohort: values.cohort,
      force: values.force
    }
  }).then(_temp13);
}
function _temp15(data) {
  return data.map(snakeToCamelKeys);
}
function _temp16(data) {
  return snakeToCamelKeys(data);
}
function _temp17(item) {
  return item.entityId;
}
function _temp18(formData) {
  return GeneratedFactApi.entities.uploadSectorToTaxonomy({
    body: formData
  });
}
function _temp19(formData) {
  return GeneratedFactApi.entities.uploadTaxonomyAlignment({
    body: formData
  });
}
function _temp20(formData) {
  return GeneratedFactApi.entities.uploadSegmentToTaxonomy({
    body: formData
  });
}
function _temp21(data) {
  return data.result_id;
}
function _temp22(t0) {
  const {
    file,
    includeNpData,
    identifierCol,
    includeFundamentals,
    includeSectors,
    shouldEnsurePublicEntities
  } = t0;
  return GeneratedFactApi.entities.lookupEntities({
    body: {
      security_file: file
    },
    query: {
      identifier_type: "isin",
      include_np_data: includeNpData,
      output_format: ExportFormats.CSV,
      identifier_col: identifierCol,
      include_fundamentals: includeFundamentals,
      include_sectors: includeSectors,
      should_ensure_public_entities: shouldEnsurePublicEntities
    }
  }).then(_temp21);
}
function _temp23(data) {
  return data.result_id;
}
function _temp24(values) {
  return GeneratedFactApi.entities.lookupSfdr({
    body: {
      security_file: values.inputFile,
      identifier_type: "isin",
      identifier_col: values.isinColumnName
    }
  }).then(_temp23);
}